import type { Language } from '../../../../../generated/graphql-manager';

import { Paragraph, Tab, Tabs } from '@movingimage-evp/mi-ui-component-library';
import { useTranslation } from 'react-i18next';

import { getLanguageKey } from '../../../../../utils';
import { LanguageEditors } from '../language-editors';

import styles from './description-editors.module.css';

type DescriptionType = 'preLive' | 'live' | 'postLive';

type Props = {
  primaryLanguage?: Language;
  languages: Language[];
  disabled?: boolean;
  unsavedChanges: Record<string, { prelive: boolean; live: boolean; postlive: boolean }>;
  editorRefreshKeys: number[];
  getDescription: (language: string, type: DescriptionType) => string | undefined;
  onDescriptionChange: (value: string, language: string, type: DescriptionType) => void;
  onTabChange: () => void;
};

export function DescriptionEditors({
  primaryLanguage,
  languages,
  disabled,
  unsavedChanges,
  editorRefreshKeys,
  getDescription,
  onDescriptionChange,
  onTabChange,
}: Props) {
  const { t } = useTranslation();

  return (
    <div className={styles.wrapper}>
      <Paragraph>{t('manager.webcastSetup.eventDescription.heading')}</Paragraph>

      {primaryLanguage && languages.length === 1 && (
        <LanguageEditors
          language={primaryLanguage.code}
          disabled={disabled}
          preLiveDescription={getDescription(primaryLanguage.code, 'preLive')}
          preLiveDescriptionChanged={unsavedChanges[primaryLanguage.code]?.prelive}
          liveDescription={getDescription(primaryLanguage.code, 'live')}
          liveDescriptionChanged={unsavedChanges[primaryLanguage.code]?.live}
          postLiveDescription={getDescription(primaryLanguage.code, 'postLive')}
          postLiveDescriptionChanged={unsavedChanges[primaryLanguage.code]?.postlive}
          editorRefreshKeys={editorRefreshKeys}
          onDescriptionChange={onDescriptionChange}
        />
      )}

      {languages.length > 1 && (
        <Tabs compact={languages.length <= 4} onSelectTab={onTabChange}>
          {languages.map((language) => {
            const isPrimaryLanguage = language === primaryLanguage;

            const translatedLanguage = t(
              `manager.webcastSetup.language.supportedLanguages.${getLanguageKey(language.code)}`
            );

            const label = isPrimaryLanguage
              ? t('manager.webcastSetup.eventDescription.primaryLanguage', {
                  primaryLanguage: translatedLanguage,
                })
              : translatedLanguage;

            const unsavedChangesForLanguage =
              unsavedChanges[language.code]?.prelive ||
              unsavedChanges[language.code]?.live ||
              unsavedChanges[language.code]?.postlive;

            return (
              <Tab
                label={`${label}${unsavedChangesForLanguage ? ' *' : ''}`}
                key={language.code}
                data-testid={`event-description-tab-${language.code}`}
              >
                <LanguageEditors
                  language={language.code}
                  disabled={disabled}
                  preLiveDescription={getDescription(language.code, 'preLive')}
                  preLiveDescriptionChanged={unsavedChanges[language.code]?.prelive}
                  liveDescription={getDescription(language.code, 'live')}
                  liveDescriptionChanged={unsavedChanges[language.code]?.live}
                  postLiveDescription={getDescription(language.code, 'postLive')}
                  postLiveDescriptionChanged={unsavedChanges[language.code]?.postlive}
                  editorRefreshKeys={editorRefreshKeys}
                  onDescriptionChange={onDescriptionChange}
                  className={styles.multilanguageWrapper}
                />
              </Tab>
            );
          })}
        </Tabs>
      )}
    </div>
  );
}
